/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { createFormContext, zodResolver, type UseFormReturnType } from '@mantine/form';
import { z } from 'zod';

import {
  refinedFinetuningDatasetSchema,
  refinedFinetuningModelSchema,
} from '@fai/friendli-suite/features/products/dedicated-endpoints/endpoints/form-context/schema/refiner';
import { DedicatedModelType } from '@fai/friendli-suite/features/types/dedicated-endpoints/models';

const batchSizeMustBeMultiplyOf = 16;

const finetuningFormSchema = z.object({
  name: z.string().min(1, {
    message: 'Finetuning job name must contain at least 1 character(s).',
  }),

  model: refinedFinetuningModelSchema,
  dataset: refinedFinetuningDatasetSchema,

  wandb: z.object({
    project: z.string().optional(),
  }),

  hyperparameters: z
    .object({
      learningRate: z
        .number({ required_error: "'Learning rate' is required." })
        .positive({ message: "'Learning rate' should be positive." }),
      batchSize: z
        .number({ required_error: "'Batch size' is required." })
        .int()
        .positive({ message: "'Batch size' should be positive." }),

      totalNumberOfTraining: z
        .object({
          type: z.union([z.literal('numTrainEpochs'), z.literal('maxSteps')]),
          numTrainEpochs: z.number().optional(),
          maxSteps: z.number().optional(),
        })
        .refine(
          (schema) => {
            switch (schema.type) {
              case 'numTrainEpochs': {
                return z.number().int().positive().safeParse(schema.numTrainEpochs).success;
              }
              case 'maxSteps': {
                return z.number().int().positive().safeParse(schema.maxSteps).success;
              }

              default: {
                return false;
              }
            }
          },
          { message: "'Total number of training' is invalid." }
        ),

      evalSteps: z
        .number({ required_error: "'Evaluation steps' is required." })
        .int()
        .positive({ message: "'Evaluation steps' should be positive." }),
      loraR: z.number().int().positive({ message: "'LoRA rank' should be positive." }).optional(),
      loraAlpha: z
        .number()
        .int()
        .nonnegative({ message: "'LoRA alpha' should be non-negative." })
        .optional(),
      loraDropout: z
        .number()
        .nonnegative({ message: "'LoRA dropout' should be non-negative." })
        .optional(),
    })
    .refine(
      (schema) => {
        const batchSize = schema.batchSize;
        const isBatchSizeError = batchSize % batchSizeMustBeMultiplyOf !== 0;

        return !isBatchSizeError;
      },
      { message: 'Batch size is invalid.' }
    ),
});
const initialValues: FinetuningFormSchema = {
  name: '',
  model: {
    type: null,
    huggingFace: {
      repositoryName: '',
      repositoryRevision: null,
    },
    wandb: {
      artifactName: '',
    },
    native: {
      modelId: '',
      modelName: '',
    },
  },
  // NOTE:
  // We allows only native dataset for ftj creation.
  // But old ftj is created with hf/wandb dataset, so let's keep them in schema.
  // New ftj will not be created and updated without native dataset.
  dataset: {
    type: DedicatedModelType.Native,
    huggingFace: {
      repositoryName: '',
      repositoryRevision: null,
    },
    wandb: {
      artifactName: '',
    },
    native: {
      modelId: '',
      modelName: '',
    },
  },
  wandb: {
    project: '',
  },
  hyperparameters: {
    batchSize: 16,
    learningRate: 0.0001,
    totalNumberOfTraining: {
      type: 'numTrainEpochs',
      numTrainEpochs: 1,
      maxSteps: 1000,
    },
    evalSteps: 300,
    loraR: 16,
    loraAlpha: 32,
    loraDropout: 0.1,
  },
};

const [FormProvider, useFormContext, useForm] = createFormContext<FinetuningFormSchema>();

const validate = zodResolver(finetuningFormSchema);

type FinetuningFormSchema = z.infer<typeof finetuningFormSchema>;
type FinetuningFormType = UseFormReturnType<FinetuningFormSchema>;

export type { FinetuningFormType, FinetuningFormSchema };
export { FormProvider, useFormContext, useForm, validate, initialValues };
